/**
 * Request Deduplicator - Prevents duplicate concurrent API requests
 * When multiple components request the same endpoint simultaneously,
 * only one request is made and the result is shared with all callers.
 */

// Store for pending requests
const pendingRequests = new Map();

/**
 * Deduplicated fetch wrapper
 * @param {string} key - Unique key for the request (usually the URL)
 * @param {Function} fetchFn - Async function that performs the actual fetch
 * @param {number} ttl - Time-to-live for cached result in ms (default: 100ms)
 * @returns {Promise} - Promise resolving to the fetch result
 */
export const deduplicatedFetch = async (key, fetchFn, ttl = 100) => {
  // Check if there's already a pending request for this key
  const pending = pendingRequests.get(key);
  if (pending) {
    return pending.promise;
  }

  // Create new request
  const promise = fetchFn();
  
  // Store the promise
  pendingRequests.set(key, { promise, timestamp: Date.now() });

  try {
    const result = await promise;
    // Keep in cache briefly to deduplicate rapid successive calls
    setTimeout(() => {
      pendingRequests.delete(key);
    }, ttl);
    return result;
  } catch (error) {
    // Remove failed requests immediately
    pendingRequests.delete(key);
    throw error;
  }
};

/**
 * Clear all pending requests (useful for testing or logout)
 */
export const clearPendingRequests = () => {
  pendingRequests.clear();
};

export default deduplicatedFetch;
